<script setup lang="ts">
import { computed } from "vue";
import { formatGib, formatGrouped, formatVolume } from "../format";
import { quotaCycle } from "../quota";
import type { Overview } from "../types/api";
import AppIcon from "./AppIcon.vue";
import DailyBars, { type DayBar } from "./DailyBars.vue";
import SectionBlock from "./SectionBlock.vue";

const props = defineProps<{ data: Overview; now: number }>();
defineEmits<{ "view-traffic": [] }>();

const DAY_MS = 86_400_000;
const WINDOW_DAYS = 30;
const isoDay = (time: number) => new Date(time).toISOString().slice(0, 10);

const cycle = computed(() => (props.data.bandwidth_quota ? quotaCycle(props.data.bandwidth_quota, props.now) : null));

const today = computed(() => isoDay(props.now));

/** The last 30 UTC days, oldest first, with gaps left as gaps. */
const days = computed<DayBar[]>(() => {
  const byDate = new Map(props.data.daily_traffic.map((row) => [row.date, row]));
  const end = Date.parse(`${today.value}T00:00:00Z`);
  return Array.from({ length: WINDOW_DAYS }, (_, index) => {
    const date = isoDay(end - (WINDOW_DAYS - 1 - index) * DAY_MS);
    const row = byDate.get(date);
    return { date, total: row?.total_bytes ?? null, rx: row?.rx_bytes ?? null, tx: row?.tx_bytes ?? null };
  });
});

const hasDaily = computed(() => days.value.some((day) => day.total != null));
const todayTotal = computed(() => days.value[days.value.length - 1]?.total ?? null);

/** First UTC day of the current billing cycle: the reset instant moved back one month. */
const cycleStart = computed(() => {
  const reset = props.data.bandwidth_quota?.reset_at;
  if (!reset) return null;
  const start = new Date(reset * 1000);
  start.setUTCMonth(start.getUTCMonth() - 1);
  return isoDay(start.getTime());
});

const pace = computed(() => {
  const info = cycle.value;
  if (!info) return "";
  const difference = info.used - info.quota * (info.elapsedPercent / 100);
  const amount = `${formatGib(Math.abs(difference), 0)} GB`;
  return difference <= 0 ? `比匀速使用少 ${amount}` : `比匀速使用多 ${amount}`;
});

const resetText = computed(() => {
  const reset = props.data.bandwidth_quota?.reset_at;
  if (!reset) return "";
  const date = new Date(reset * 1000);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getMonth() + 1}月${date.getDate()}日 ${pad(date.getHours())}:${pad(date.getMinutes())}`;
});

const syncText = computed(() => {
  const quota = props.data.bandwidth_quota;
  if (!quota) return props.data.traffic_interface ? `${props.data.traffic_interface} · vnStat` : "";
  const minutes = quota.updated_at ? Math.max(0, Math.floor((props.now / 1000 - quota.updated_at) / 60)) : null;
  const age = minutes == null ? "尚未" : minutes < 1 ? "刚刚" : minutes < 120 ? `${minutes} 分钟前` : `${Math.floor(minutes / 60)} 小时前`;
  return quota.error ? `额度同步失败，仍是${age}的数据` : `KiwiVM · ${age}同步`;
});

const sourceNames = computed(
  () => new Map((props.data.traffic_source_catalog ?? []).map((item) => [item.source, item.label]))
);

/** Where today's proxied traffic went: the three largest identified destinations. */
const topSources = computed(() => {
  const rows = props.data.traffic_sources.filter((row) => row.date === today.value && row.source !== "other");
  return rows
    .filter((row) => row.total_bytes > 0)
    .sort((a, b) => b.total_bytes - a.total_bytes)
    .slice(0, 3)
    .map((row) => ({
      source: row.source,
      name: (sourceNames.value.get(row.source) ?? row.source).split(" / ")[0],
      volume: formatVolume(row.total_bytes)
    }));
});
</script>

<template>
  <SectionBlock title="流量" :note="syncText">
    <div v-if="cycle" class="quota" :class="`quota--${cycle.level}`">
      <p class="quota__figure">
        <span class="quota__label">本周期已用</span>
        <strong>{{ formatGib(cycle.used) }}</strong>
        <span class="quota__of num">/ {{ formatGib(cycle.quota, 0) }} GB</span>
      </p>

      <div class="quota__meter">
        <div
          class="meter"
          role="meter"
          aria-label="本周期流量使用率"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="Math.round(cycle.percent)"
          :aria-valuetext="`已用 ${cycle.percent.toFixed(1)}%，周期时间已过 ${cycle.elapsedPercent.toFixed(0)}%`"
        >
          <span class="meter__fill" :style="{ width: `${Math.min(100, cycle.percent)}%` }"></span>
          <i class="meter__now" :style="{ left: `${cycle.elapsedPercent}%` }"></i>
        </div>
        <div class="quota__scale" :class="{ 'quota__scale--early': cycle.elapsedPercent < 25 }" aria-hidden="true">
          <span class="quota__used num">已用 {{ cycle.percent.toFixed(1) }}%</span>
          <span
            class="quota__today"
            :style="{ left: `clamp(0.8rem, ${cycle.elapsedPercent}%, calc(100% - 0.8rem))` }"
          >今天</span>
        </div>
      </div>

      <dl class="facts">
        <div>
          <dt>剩余</dt>
          <dd class="num">{{ formatGib(cycle.remaining) }}<span class="unit">GB</span></dd>
          <dd v-if="cycle.daysLeft > 0.05" class="facts__note num">
            日均可用 {{ formatGrouped(cycle.remaining / 2 ** 30 / Math.max(cycle.daysLeft, 1), 0) }} GB
          </dd>
        </div>
        <div>
          <dt>重置</dt>
          <dd class="num">{{ cycle.daysLeft.toFixed(1) }}<span class="unit">天后</span></dd>
          <dd class="facts__note num">{{ resetText }}</dd>
        </div>
        <div>
          <dt>按当前速度，周期末</dt>
          <dd class="num">
            <template v-if="cycle.projected != null">{{ formatGib(cycle.projected, 0) }}<span class="unit">GB</span></template>
            <template v-else>—</template>
          </dd>
          <dd class="facts__note">{{ cycle.projected == null ? "周期刚开始，暂不预估" : pace }}</dd>
        </div>
      </dl>
    </div>

    <div v-if="hasDaily" class="daily">
      <div class="daily__head">
        <h3>每日用量</h3>
        <p>
          近 {{ WINDOW_DAYS }} 天 · UTC 自然日<template v-if="cycle && cycleStart">，浅色属于上个周期</template>
        </p>
        <p v-if="todayTotal != null" class="daily__today num">今日 {{ formatGib(todayTotal) }} GiB</p>
      </div>
      <DailyBars :days="days" :today="today" :emphasis-from="cycle ? cycleStart : null" />
    </div>
    <p v-else class="daily__empty">暂无每日记录，等待 vnStat 采集。</p>

    <p class="traffic-foot">
      <span v-if="topSources.length" class="traffic-foot__sources">
        今日去向：<template v-for="(item, index) in topSources" :key="item.source"
          ><template v-if="index">、</template>{{ item.name }} <span class="num">{{ item.volume }}</span></template
        >
      </span>
      <button class="text-link" type="button" @click="$emit('view-traffic')">
        每日明细与来源排行<AppIcon name="arrow-right" :size="14" />
      </button>
    </p>
  </SectionBlock>
</template>

<style scoped>
.quota { display: grid; gap: 1.25rem; margin-bottom: 2.5rem; --level: var(--accent); --level-track: var(--accent-soft); }
.quota--warning { --level: var(--warn); --level-track: color-mix(in srgb, var(--warn) 24%, var(--bg)); }
.quota--critical { --level: var(--bad); --level-track: color-mix(in srgb, var(--bad) 22%, var(--bg)); }

.quota__figure { display: flex; flex-wrap: wrap; gap: 0 0.6rem; align-items: baseline; }
.quota__label { flex-basis: 100%; margin-bottom: 0.15rem; color: var(--ink-3); font-size: 0.8125rem; }
.quota__figure strong { font-size: clamp(2rem, 5vw, 2.5rem); font-weight: 500; line-height: 1.1; letter-spacing: -0.025em; }
.quota__of { color: var(--ink-3); font-size: 0.9375rem; }

/* The unfilled track is a lighter step of the fill's own hue, so the state reads across the bar. */
.meter { position: relative; height: 0.5rem; border-radius: 999px; background: var(--level-track); }
.meter__fill { display: block; height: 100%; border-radius: inherit; background: var(--level); }
.meter__now {
  position: absolute;
  top: -0.3rem;
  bottom: -0.3rem;
  width: 2px;
  margin-left: -1px;
  border-radius: 1px;
  background: var(--ink);
  box-shadow: 0 0 0 2px var(--bg);
}
.quota__scale { position: relative; height: 1.25rem; margin-top: 0.5rem; color: var(--ink-3); font-size: 0.8125rem; }
/* Early in a cycle the "today" marker sits at the left, so the usage label moves right. */
.quota__scale--early .quota__used { position: absolute; right: 0; }
.quota__today { position: absolute; top: 0; color: var(--ink-2); white-space: nowrap; transform: translateX(-50%); }

.facts { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; }
.facts dt { color: var(--ink-3); font-size: 0.8125rem; }
.facts dd { margin-top: 0.15rem; font-size: 1.25rem; font-weight: 500; letter-spacing: -0.01em; }
.facts .facts__note { margin-top: 0.1rem; color: var(--ink-3); font-size: 0.8125rem; font-weight: 400; letter-spacing: 0; }

.daily { display: grid; gap: 1.75rem; }
.daily__head { display: flex; flex-wrap: wrap; gap: 0.25rem 0.75rem; align-items: baseline; }
.daily__head h3 { font-size: 0.9375rem; }
.daily__head p { color: var(--ink-3); font-size: 0.8125rem; }
.daily__head .daily__today { margin-left: auto; color: var(--ink-2); }
.daily__empty { color: var(--ink-3); font-size: 0.875rem; }

.traffic-foot {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.5rem;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 1.5rem;
  color: var(--ink-2);
  font-size: 0.875rem;
}
.traffic-foot__sources { min-width: 0; }

@media (prefers-reduced-motion: no-preference) {
  .meter__fill { transition: width 400ms ease; }
}

@media (max-width: 640px) {
  .facts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .facts div:last-child { grid-column: 1 / -1; }
  /* On its own line and to the left, clear of the label over the tallest column. */
  .daily__head .daily__today { flex-basis: 100%; margin-left: 0; }
}
</style>
